import cleaningSchema from "./Schema/cleaningSchema";
import cooksSchema from "./Schema/cooksSchema";
import doulaSchema from "./Schema/doulaSchema";
import housekeepingSchema from "./Schema/housekeepingSchema";
import nanniesSchema from "./Schema/nanniesSchema";
import salonSchema from "./Schema/salonSchema";

export type ServiceType
  = 'cooks'
  | 'nannies'
  | 'housekeeping'
  | 'cleaning'
  | 'salon'
  | 'doula';

export interface FieldConfig {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'date';
  note?: string;
  required?: boolean;
  options?: string[];
  group?: string;
  multiple?: boolean;
  conditional?: {
    field: string;
    value: any;
  };
  extraProps?: Record<string, any>;
  warning?: {
    message: string;
    triggerValues: any[];
  };
  /** Freeze field value based on another field (e.g., package selection).
   * sourceField: the formData key to check (e.g., "subModality", "cleaningType")
   * valueMapping: maps sourceField values → locked field values */
  lockedBy?: {
    sourceField: string;
    valueMapping: Record<string, any>;
  };
}

export const serviceTypeMapping: Record<string, ServiceType> = {
  'Cooking Service': 'cooks',
  'Nanny Service': 'nannies',
  'Nanny': 'nannies',
  'Housekeeping Service': 'housekeeping',
  'Cleaning Service': 'cleaning',
  'Salon Service': 'salon',
  'Doula Service': 'doula',
};

export const fieldsConfig: Record<ServiceType, FieldConfig[]> = {
  cooks: [
    { name: "cuisine", label: "Cuisine", type: "select", required: true, options: ["Vietnamese", "European", "Persian", "East African", "Italian cuisine", "Japanese cuisine", "French cuisine", "French", "Indian cuisine", "Thai cuisine", "Mexican cuisine", "Greek cuisine", "Asian cuisines", "Korean Chinese cuisine", "Turkish cuisine", "Chinese", "Spanish cuisine", "Chinese cuisine", "Lebanese cuisine"] },
    { name: "vegNonVeg", label: "Veg or Non-Veg", type: "select", required: true, options: ["Veg", "Non-Veg"] },
    { name: "people", label: "No. of People", type: "number", required: true },
    { name: "allergies", label: "Allergies", type: "select", options: ["Yes", "No"], required: true },
    {
      name: "allergyDetails",
      label: "Please specify allergies",
      type: "text",
      required: false,
      conditional: { field: "allergies", value: "Yes" }
    },
    { name: "diet", label: "Diet/Menu Preferences", type: "text", required: true },
    { name: "providingRecipe", label: "Are you providing the recipe?", type: "select", options: ["Yes", "No"], required: true },
    {
      name: "menuDetails",
      label: "Please provide menu/recipe details",
      type: "text",
      required: false,
      conditional: { field: "providingRecipe", value: "Yes" }
    },
  ],
  nannies: [
    { name: "kids", label: "Number of Kids", type: "number", required: true },
    {
      name: "ages",
      label: "Ages of Kids",
      type: "text",
      required: true,
      // conditional: { field: "kids", value: (val: any) => val > 1 }
    },
    { name: "specialNeeds", label: "Special Need Kids", type: "select", options: ["Yes", "No"], required: true,
      lockedBy: {
        sourceField: "subModality",
        valueMapping: { "Special Needs Kid": "Yes", "Standard Service": "No" }
      }
    },
    { name: "allergies", label: "Allergies", type: "select", options: ["Yes", "No"], required: true },
    {
      name: "allergyDetails",
      label: "Please specify allergies",
      type: "text",
      required: false,
      conditional: { field: "allergies", value: "Yes" }
    },
    { name: "diet", label: "Diet/Menu Preferences", type: "text", required: true },
    { name: "emergency1Name", label: "Name", type: "text", group: "Emergency Contact 1", required: true },
    { name: "emergency1Phone", label: "Phone Number", type: "number", group: "Emergency Contact 1", required: true, extraProps: { maxLength: 11 } },
    { name: "emergency1Relation", label: "Relationship", type: "text", group: "Emergency Contact 1", required: true },
    { name: "emergency2Name", label: "Name", type: "text", group: "Emergency Contact 2" },
    { name: "emergency2Phone", label: "Phone Number", type: "number", group: "Emergency Contact 2", extraProps: { maxLength: 11 } },
    { name: "emergency2Relation", label: "Relationship", type: "text", group: "Emergency Contact 2" },
  ],
  housekeeping: [
    { name: "homeType", label: "Type of Home", type: "select", options: ["Condo", "Townhouse", "Detached", "Duplex", "Mobile Home"], required: true },
    { name: "rooms", label: "No. of Rooms", type: "number", required: true, note: "Minimum booking of two (2) hours is required for a three-bedroom, two-bathroom property. An additional thirty (30) minutes will be allocated for each additional bedroom or bathroom." },
    { name: "washrooms", label: "No. of Washrooms", type: "number", required: true },
    { name: "supplies", label: "Include Supplies?", type: "select", options: ["Yes", "No"], required: true },

  ],
  cleaning: [
    { name: "homeType", label: "Type of Home", type: "select", options: ["Condo", "Townhouse", "Detached", "Duplex", "Mobile Home"], required: true },
    { name: "rooms", label: "No. of Rooms", type: "number", required: true, note: "Minimum booking of two (2) hours is required for a three-bedroom, two-bathroom property. An additional thirty (30) minutes will be allocated for each additional bedroom or bathroom." },
    { name: "washrooms", label: "No. of Washrooms", type: "number", required: true },
    { name: "supplies", label: "Include Supplies?", type: "select", options: ["Yes", "No"], required: true },
    {
      name: "cleaningType",
      label: "Type of Cleaning",
      type: "select",
      options: ["Routine", "Deep Cleaning", "Move in/Move out"],
      required: true,
      warning: {
        message: "Additional charges are applicable for this service",
        triggerValues: ["Deep Cleaning", "Move in/Move out"]
      }
    },
  ],
  salon: [
    { name: "people", label: "No. of People", type: "number", required: true },
    {
      name: "service",
      label: "Service Required",
      type: "select",
      options: ["Hair cutting", "Styling", "Colouring", "Manicures", "Pedicures", "Waxing", "Facials", "Heena (Mehndi)", "Makeup Application", "Others"],
      required: true,
      multiple: true
    },
    {
      name: "otherService",
      label: "Please specify other service",
      type: "text",
      required: false,
      conditional: { field: "service", value: "Others" }
    },
    { name: "supplies", label: "Include Supplies?", type: "select", options: ["Yes", "No"], required: true },
    { name: "allergies", label: "Allergies", type: "select", options: ["Yes", "No"], required: true },
    {
      name: "allergyDetails",
      label: "Please specify allergies",
      type: "text",
      required: false,
      conditional: { field: "allergies", value: "Yes" }
    },
  ],
  doula: [
    { name: "expecting", label: "Are you expecting to be a mom?", type: "select", options: ["Yes", "No"], required: true },
    {
      name: "dueDate", label: "Due Date / Expected Delivery", type: "date",
      conditional: { field: "expecting", value: "Yes" }
    },
    {
      name: "isBookingForSomeoneElse",
      label: "Are you booking for someone else?",
      type: "select",
      options: ["Yes", "No"],
      conditional: { field: "expecting", value: "No" },
      required: true
    },
    {
      name: "relationship",
      label: "Relationship with customer",
      type: "text",
      conditional: { field: "isBookingForSomeoneElse", value: "Yes" },
      required: true
    },
    {
      name: "expectingMotherName",
      label: "Name of expecting mother",
      type: "text",
      conditional: { field: "isBookingForSomeoneElse", value: "Yes" },
      required: true
    },
    {
      name: "expectingMotherPhone",
      label: "Phone number",
      type: "number",
      extraProps: { maxLength: 11 },
      conditional: { field: "isBookingForSomeoneElse", value: "Yes" },
      required: true
    },
    { name: "kids", label: "Number of kids in the house?", type: "number" },
    {
      name: "motherName",
      label: "Mother's Name",
      type: "text",
      required: true,
      conditional: { field: "expecting", value: "Yes" }
    },
    { name: "birthLocation", label: "Birth Location", type: "text", required: true },
    { name: "supportType", label: "Type of Support", type: "select", options: ["Prenatal", "Labor & Birth", "Postpartum"], required: true },
    { name: "medicalConcerns", label: "Medical Conditions/Concerns(If any)", type: "text" },
    { name: "partnerName", label: "Partner's Name", type: "text" },
    { name: "partnerInvolvement", label: "Partner Involvement", type: "select", options: ["Active", "Partial", "Minimal", "Not Involved"] },
    { name: "emergency1Name", label: "Name", type: "text", group: "Emergency Contact 1", required: true },
    { name: "emergency1Phone", label: "Phone Number", type: "number", group: "Emergency Contact 1", required: true, extraProps: { maxLength: 11 } },
    { name: "emergency1Relation", label: "Relationship", type: "text", group: "Emergency Contact 1", required: true },
    { name: "emergency2Name", label: "Name", type: "text", group: "Emergency Contact 2" },
    { name: "emergency2Phone", label: "Phone Number", type: "number", group: "Emergency Contact 2", extraProps: { maxLength: 11 } },
    { name: "emergency2Relation", label: "Relationship", type: "text", group: "Emergency Contact 2" },
  ],
};

// Export the combined schemas
export const servicesSchemas = {
  cooks: cooksSchema,
  nannies: nanniesSchema,
  housekeeping: housekeepingSchema,
  cleaning: cleaningSchema,
  salon: salonSchema,
  doula: doulaSchema,
};

// function to get validation schema for a service type
export const getServiceValidationSchema = (serviceType: ServiceType) => {
  return servicesSchemas[serviceType] || null;
};

// function to validate service-specific fields
export const validateServiceFields = (serviceType: ServiceType, data: Record<string, any>): { isValid: boolean; errors: Record<string, string> } => {
  const schema = servicesSchemas[serviceType];
  if (!schema) return { isValid: true, errors: {} };

  try {
    schema.validateSync(data, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (error: any) {
    const errors: Record<string, string> = {};
    error.inner.forEach((err: any) => {
      errors[err.path] = err.message;
    });
    return { isValid: false, errors };
  }
};

// function to validate a single field
export const validateSingleField = (serviceType: ServiceType, fieldName: string, data: Record<string, any>): string | null => {
  const schema = servicesSchemas[serviceType];
  if (!schema) return null;

  try {
    // We need to use validateSyncAt to validate a specific field against the schema
    // validateSyncAt validates the value at the path, but we need to pass the whole data object
    // so that references (refs) and context work correctly.
    schema.validateSyncAt(fieldName, data);
    return null; // No error
  } catch (error: any) {
    return error.message;
  }
};
